import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { toast } from "sonner";
import {
  Activity,
  Clock,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Check,
  X,
  Cpu,
  Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color }) => {
  const colorClasses = {
    default: "text-[#e0e0e8]",
    green: "text-[#6ee7b7]",
    orange: "text-[#f59e0b]",
    red: "text-[#ef4444]",
    blue: "text-[#60a5fa]",
  };

  return (
    <Card className="bg-[#12121a] border-[#2a2a3a]" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#7a7a8e] mb-1">
              {title}
            </p>
            <p className={`font-mono text-3xl font-semibold ${colorClasses[color] || colorClasses.default}`}>
              {value.toLocaleString()}
            </p>
          </div>
          <div className={`w-10 h-10 rounded flex items-center justify-center bg-[#1a1a26] ${colorClasses[color] || colorClasses.default}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Custom Tooltip for Chart
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#12121a] border border-[#2a2a3a] p-3 rounded">
        <p className="font-mono text-xs text-[#7a7a8e]">{label}</p>
        <p className="font-mono text-sm text-[#6ee7b7]">{payload[0].value} items</p>
      </div>
    );
  }
  return null;
};

// Flagged Item Card
const FlaggedItemCard = ({ item, onApprove, onReject }) => {
  const [loading, setLoading] = useState(false);

  const handleAction = async (action) => {
    setLoading(true);
    try {
      if (action === 'approve') {
        await onApprove(item.id);
      } else {
        await onReject(item.id);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 bg-[#1a1a26] border border-[#2a2a3a] rounded" data-testid={`flagged-item-${item.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            {(item.confidence ?? item.importance_score) != null && (
              <Badge className={`font-mono text-[10px] rounded px-2 py-0.5 ${
                (item.confidence ?? item.importance_score) >= 8 ? 'bg-[#6ee7b7]/20 text-[#6ee7b7] border-[#6ee7b7]/30' :
                (item.confidence ?? item.importance_score) >= 5 ? 'bg-[#f59e0b]/20 text-[#f59e0b] border-[#f59e0b]/30' :
                'bg-[#ef4444]/20 text-[#ef4444] border-[#ef4444]/30'
              }`}>
                {item.confidence ?? item.importance_score}/10
              </Badge>
            )}
            {item.category && (
              <Badge className="font-mono text-[10px] bg-[#60a5fa]/20 text-[#60a5fa] border-[#60a5fa]/30 rounded px-2 py-0.5 uppercase">
                {item.category.replace(/_/g, ' ')}
              </Badge>
            )}
          </div>
          <p className="text-sm text-[#e0e0e8] line-clamp-2">
            {item.summary || item.raw_text}
          </p>
          <p className="font-mono text-xs text-[#7a7a8e] mt-2">
            {item.source_author && `@${item.source_author} • `}
            {item.batch_date}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => handleAction('approve')}
            disabled={loading}
            className="h-8 bg-[#6ee7b7]/20 hover:bg-[#6ee7b7]/30 text-[#6ee7b7] border border-[#6ee7b7]/30"
            data-testid={`approve-${item.id}`}
          >
            <Check className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            onClick={() => handleAction('reject')}
            disabled={loading}
            className="h-8 bg-[#ef4444]/20 hover:bg-[#ef4444]/30 text-[#ef4444] border border-[#ef4444]/30"
            data-testid={`reject-${item.id}`}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_items: 0,
    processed_count: 0,
    unprocessed_count: 0,
    flagged_count: 0,
    processed_today: 0
  });
  const [dailyStats, setDailyStats] = useState([]);
  const [flaggedItems, setFlaggedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const processingRef = useRef(false);

  const fetchData = async () => {
    try {
      const [statsRes, dailyRes, flaggedRes] = await Promise.all([
        api.get('/stats'),
        api.get('/stats/daily?days=14'),
        api.get('/items/flagged?limit=5')
      ]);
      
      setStats(statsRes.data);
      setDailyStats(dailyRes.data.map(d => ({
        date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        count: d.count
      })));
      setFlaggedItems(flaggedRes.data);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleApprove = async (id) => {
    try {
      await api.patch(`/items/${id}`, { status: 'approved', flagged_for_review: false });
      toast.success("Item approved");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve item");
    }
  };

  const handleReject = async (id) => {
    try {
      await api.patch(`/items/${id}`, { status: 'rejected', flagged_for_review: false });
      toast.success("Item rejected");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject item");
    }
  };

  const handleProcessQueue = async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    setProcessing(true);
    let processed = 0;
    let errors = 0;

    try {
      // Drain the queue one item at a time (same approach as Process page)
      while (processingRef.current) {
        const res = await api.post('/process?limit=1');
        const data = res.data;
        if (!data || data.processed_count === 0) break;
        processed += data.processed_count;
        errors = 0; // reset on success
      }
      toast.success(`Processed ${processed} item${processed !== 1 ? 's' : ''}`);
      fetchData();
    } catch (err) {
      errors++;
      if (errors >= 3) {
        toast.error("Processing stopped after repeated errors");
      }
    } finally {
      processingRef.current = false;
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#7a7a8e]">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-semibold">Dashboard</h1>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleProcessQueue}
          disabled={processing}
          className="text-[#7a7a8e] hover:text-[#e0e0e8] border border-[#2a2a3a] hover:border-[#3a3a4a] font-mono text-xs"
          title="Manually trigger AI processing of any queued items"
        >
          {processing
            ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Processing...</>
            : <><Cpu className="w-3.5 h-3.5 mr-1.5" />Process Queue</>
          }
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title="Total Items" value={stats.total_items} icon={Activity} color="default" />
        <StatCard title="Processed Today" value={stats.processed_today} icon={CheckCircle} color="green" />
        <StatCard title="Pending" value={stats.unprocessed_count} icon={Clock} color="orange" />
        <StatCard title="Flagged" value={stats.flagged_count} icon={AlertTriangle} color="red" />
      </div>

      {/* Chart */}
      <Card className="bg-[#12121a] border-[#2a2a3a] mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#6ee7b7]" />
            Items Ingested (Last 14 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyStats} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3a" vertical={false} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: '#7a7a8e', fontSize: 10 }} 
                  tickLine={false}
                  axisLine={{ stroke: '#2a2a3a' }}
                />
                <YAxis 
                  tick={{ fill: '#7a7a8e', fontSize: 10 }} 
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" fill="#6ee7b7" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Flagged Items */}
      <Card className="bg-[#12121a] border-[#2a2a3a]">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-[#ef4444]" />
              Needs Review
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/browse?status=flagged')}
              className="text-[#7a7a8e] hover:text-[#e0e0e8] font-mono text-xs"
              data-testid="view-all-flagged"
            >
              View All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {flaggedItems.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-8 h-8 text-[#6ee7b7] mx-auto mb-2" />
              <p className="text-[#7a7a8e] font-mono text-sm">No items need review</p>
            </div>
          ) : (
            <div className="space-y-3">
              {flaggedItems.map(item => (
                <FlaggedItemCard 
                  key={item.id} 
                  item={item} 
                  onApprove={handleApprove}
                  onReject={handleReject}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
